/** Thin fetch wrapper: attaches the bearer token, normalizes control-plane errors. */

import { config } from './config';
import { currentAccessToken, userManager } from './auth';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await currentAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${config.apiBase}${path}`, { ...init, headers });

  if (res.status === 401) {
    // The token was rejected. Drop it so the app falls back to the login gate.
    await userManager.removeUser().catch(() => undefined);
    throw new ApiError(401, 'Your session expired — please sign in again.');
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      /* leave body null for non-JSON responses */
    }
  }

  const errorish = body as { ok?: boolean; error?: string } | null;
  if (!res.ok || errorish?.ok === false) {
    throw new ApiError(res.status, errorish?.error ?? `Request failed (${res.status})`);
  }
  return body as T;
}
